import { ReportType, type ReportDto } from "@edlab-fms/shared";
import { formatMoney } from "../../lib/format/money";
import { Logo } from "../../components/Logo";
import {
  formatReportAmount,
  formatReportDateCell,
  isAmountColumn,
  monthHeading,
  preparedLabel,
  primaryStats,
  reportFooterNote,
  reportSubtitle,
  summaryValue,
  throughLabel,
} from "./report-format";

interface ReportPreviewDisplayProps {
  report: ReportDto;
}

function formatCell(cell: string, column: string | undefined, reportType: ReportDto["meta"]["reportType"]): string {
  const label = column?.toLowerCase() ?? "";
  if (label === "date") {
    return formatReportDateCell(cell);
  }
  if (label.includes("count") || label.includes("gaps")) {
    return cell;
  }
  if (isAmountColumn(label)) {
    if (reportType === ReportType.DONOR && label === "paid") {
      return formatReportAmount(cell);
    }
    return formatMoney(cell);
  }
  return cell;
}

function ObligationLines({ report }: { report: ReportDto }) {
  if (report.meta.reportType === ReportType.DONOR) {
    const approved = summaryValue(report.summary, "approvedUnpaid");
    const available = summaryValue(report.summary, "availableToCommit");
    const pledge = summaryValue(report.summary, "outstandingPledge");
    return (
      <>
        <p className="report-obligation">
          Approved unpaid: <b>{approved}</b> · Available after these obligations:{" "}
          <b>{available}</b>
        </p>
        <p className="report-obligation">
          Outstanding pledge as of {throughLabel(report.meta.periodEnd).replace("Through ", "")}:{" "}
          <b>{pledge}</b>. This is pledged support that has not yet been received.
        </p>
      </>
    );
  }

  const approved = summaryValue(report.summary, "approvedUnpaid");
  const bank = summaryValue(report.summary, "bankTotal", false);
  const bankEntry = report.summary.find((entry) => entry.key === "bankTotal");

  return (
    <p className="report-obligation">
      {bankEntry ? (
        <>
          Bank &amp; cash at period end: <b>{formatMoney(bankEntry.value)}</b> ·{" "}
        </>
      ) : null}
      Approved unpaid at period end: <b>{approved}</b>
    </p>
  );
}

function ProgrammeTable({ report }: { report: ReportDto }) {
  const table =
    report.tables.find((entry) => entry.id === "programmes") ?? report.tables[0];
  if (!table) {
    return null;
  }

  const displayColumns =
    report.meta.reportType === ReportType.BOARD ||
    report.meta.reportType === ReportType.PROGRAMME
      ? ["Programme", "Annual budget", "Period income", "Period paid", "YTD income", "YTD paid"]
      : table.columns;

  const columnIndexes = displayColumns.map((column) => table.columns.indexOf(column));

  return (
    <div className="report-table-wrap">
      <table className="report-table">
        <thead>
          <tr>
            {displayColumns.map((column) => (
              <th
                key={column}
                className={isAmountColumn(column.toLowerCase()) ? "num" : undefined}
              >
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.length ? (
            table.rows.map((row, index) => (
              <tr key={`${table.id}-${index}`}>
                {columnIndexes.map((cellIndex, displayIndex) => (
                  <td
                    key={`${table.id}-${index}-${displayIndex}`}
                    className={
                      isAmountColumn(displayColumns[displayIndex]?.toLowerCase() ?? "")
                        ? "num"
                        : undefined
                    }
                  >
                    {formatCell(
                      row[cellIndex] ?? "—",
                      displayColumns[displayIndex],
                      report.meta.reportType,
                    )}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={displayColumns.length}>No rows in this section.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export function ReportPreviewDisplay({ report }: ReportPreviewDisplayProps) {
  const stats = primaryStats(report);
  const isDonor = report.meta.reportType === ReportType.DONOR;
  const utilizationTable = report.tables.find((table) => table.id === "utilization");
  const extraTables = report.tables.filter(
    (table) => table.id !== "utilization" && table.id !== "programmes" && table.id !== "bank",
  );

  return (
    <div id="report-print-area" className="report-print-area">
      <article className="report-paper">
        <div className="report-paper-title">
          <div className="flex items-start gap-4">
            <Logo size="lg" className="shadow-none" />
            <div>
            <p className="report-eyebrow">{report.meta.organization.toUpperCase()}</p>
            <h2 className="report-paper-heading">{report.meta.title}</h2>
            <p className="report-paper-subtitle">{reportSubtitle(report)}</p>
            </div>
          </div>
          <div className="report-paper-meta">
            <p>
              <b>{monthHeading(report.meta.periodEnd)}</b>
            </p>
            <p>{throughLabel(report.meta.periodEnd)}</p>
            <p className="report-paper-meta-note">Management report</p>
          </div>
        </div>

        <div className="report-stats">
          {stats.map((entry, index) => (
            <div key={entry.key}>
              <small>{entry.label}</small>
              <strong className={index === stats.length - 1 && isDonor ? "good" : undefined}>
                {formatMoney(entry.value)}
              </strong>
            </div>
          ))}
        </div>

        <ObligationLines report={report} />

        {isDonor && utilizationTable ? (
          <>
            <div className="report-section-line">
              <h3>{utilizationTable.title}</h3>
              <p>PKR</p>
            </div>
            <div className="report-table-wrap">
              <table className="report-table">
                <thead>
                  <tr>
                    {utilizationTable.columns.map((column) => (
                      <th
                        key={column}
                        className={isAmountColumn(column.toLowerCase()) ? "num" : undefined}
                      >
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {utilizationTable.rows.length ? (
                    utilizationTable.rows.map((row, index) => (
                      <tr key={`utilization-${index}`}>
                        {row.map((cell, cellIndex) => (
                          <td
                            key={`utilization-${index}-${cellIndex}`}
                            className={
                              isAmountColumn(
                                utilizationTable.columns[cellIndex]?.toLowerCase() ?? "",
                              )
                                ? "num"
                                : undefined
                            }
                          >
                            {formatCell(
                              cell,
                              utilizationTable.columns[cellIndex],
                              report.meta.reportType,
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={utilizationTable.columns.length}>
                        No payments charged to this fund in the selected month.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <ProgrammeTable report={report} />
        )}

        {extraTables.map((table) => (
          <div key={table.id}>
            <div className="report-section-line">
              <h3>{table.title}</h3>
              <p>PKR</p>
            </div>
            <div className="report-table-wrap">
              <table className="report-table">
                <thead>
                  <tr>
                    {table.columns.map((column) => (
                      <th
                        key={column}
                        className={isAmountColumn(column.toLowerCase()) ? "num" : undefined}
                      >
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {table.rows.map((row, index) => (
                    <tr key={`${table.id}-${index}`}>
                      {row.map((cell, cellIndex) => (
                        <td
                          key={`${table.id}-${index}-${cellIndex}`}
                          className={
                            isAmountColumn(table.columns[cellIndex]?.toLowerCase() ?? "")
                              ? "num"
                              : undefined
                          }
                        >
                          {formatCell(cell, table.columns[cellIndex], report.meta.reportType)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        {report.meta.managementNote ? (
          <>
            <div className="report-section-line">
              <h3>Management note</h3>
            </div>
            <p className="report-management-note">{report.meta.managementNote}</p>
          </>
        ) : null}

        <div className="report-paper-note">
          Prepared {preparedLabel(report.meta.preparedAt)} · {report.meta.organization}
          <br />
          {reportFooterNote(report)}
        </div>
      </article>
    </div>
  );
}
